import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ACHIEVEMENTS_BY_LEVEL, categoriesForLevel, mandatoryCountForLevel, type SurfLevel } from '@/lib/surf-progression';

interface SurfingJourneyCardProps {
  level: SurfLevel;
  completedIds: Set<string>;
}

/**
 * Answers "what am I good at / what's next" rather than presenting a wall of
 * identical progress bars. Fully-completed categories move into a compact
 * "Strongest Skills" list; only the categories still in progress get a
 * progress bar, capped at 3 so this stays a summary, not the full catalogue
 * (that lives on the dedicated progression page).
 */
const SurfingJourneyCard = ({ level, completedIds }: SurfingJourneyCardProps) => {
  const achievements = ACHIEVEMENTS_BY_LEVEL[level].filter((a) => !a.optional);
  const mandatoryCount = mandatoryCountForLevel(level);
  const completed = achievements.filter((a) => completedIds.has(a.id));
  const progressPct = mandatoryCount > 0 ? Math.round((completed.length / mandatoryCount) * 100) : 0;

  const categoryStats = categoriesForLevel(level).map((category) => {
    const inCategory = achievements.filter((a) => a.category === category);
    const done = inCategory.filter((a) => completedIds.has(a.id)).length;
    return { category, done, total: inCategory.length };
  });

  const strongest = categoryStats.filter((c) => c.total > 0 && c.done === c.total);
  // Categories still in progress, closest-to-done first -- surfacing the
  // most achievable next step rather than the biggest gap.
  const inProgress = categoryStats
    .filter((c) => c.done < c.total)
    .sort((a, b) => b.done / b.total - a.done / a.total)
    .slice(0, 3);

  return (
    <div className="rounded-2xl border border-border/50 bg-card px-5 py-5 sm:px-7 sm:py-6">
      <div className="flex items-baseline justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Your Surfing Journey</p>
        <span className="text-xs text-muted-foreground tabular-nums">{level} · {progressPct}%</span>
      </div>

      {strongest.length > 0 && (
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Strongest Skills</p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
            {strongest.map(({ category }) => (
              <li key={category} className="flex items-center gap-1.5 text-sm text-foreground">
                <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                {category}
              </li>
            ))}
          </ul>
        </div>
      )}

      {inProgress.length > 0 && (
        <div className={strongest.length > 0 ? 'mt-5 pt-4 border-t border-border/50' : 'mt-4'}>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">What to Work On Next</p>
          <div className="space-y-3.5">
            {inProgress.map(({ category, done, total }) => {
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const nextAchievement = achievements.find((a) => a.category === category && !completedIds.has(a.id));
              return (
                <div key={category}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm text-foreground">{category}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{done}/{total}</span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                  {nextAchievement && (
                    <Link
                      to="/my-swell-breaks/progression"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      {nextAchievement.name}
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {strongest.length === 0 && inProgress.length === 0 && (
        <p className="mt-4 text-sm text-muted-foreground">Tell us what you can already do to get started.</p>
      )}

      <Button asChild variant="ghost" size="sm" className="mt-5 gap-1.5 px-0 hover:bg-transparent hover:text-primary">
        <Link to="/my-swell-breaks/progression">
          Continue Your Progression
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </div>
  );
};

export default SurfingJourneyCard;
